import {
  GRID_IMAGE_ASSET_SOURCE_MAX_BYTES,
  GRID_IMAGE_MAX_DIMENSION,
  type DecodedImageAssetRecord,
  type ImageAssetAllocation,
  type ImageAssetReservation,
} from './protocol'

export const GRID_IMAGE_MAX_ASSETS = 4096
export const GRID_IMAGE_ENCODED_BUDGET_BYTES = 64 * 1024 * 1024
export const GRID_IMAGE_DECODED_BUDGET_BYTES = 64 * 1024 * 1024

export type BrowserImageProtocolReason = 'decode' | 'generation' | 'asset'

export class BrowserImageProtocolError extends Error {
  constructor(readonly reason: BrowserImageProtocolReason, message: string) {
    super(message)
    this.name = 'BrowserImageProtocolError'
  }
}

export class BrowserImageStaleOperationError extends Error {
  constructor() {
    super('image operation crossed a lifecycle reset')
    this.name = 'BrowserImageStaleOperationError'
  }
}

export type BrowserImageState = 'pending' | 'success' | 'failure'

export interface BrowserImageStateEvent {
  readonly assetEpoch: number
  readonly assetId: number
  readonly contentHash: string
  readonly state: BrowserImageState
}

export interface BrowserImageLease<T> {
  readonly value: T
  release(): void
}

export interface BrowserImageRenderAsset {
  readonly assetId: number
  readonly contentHash: string
  readonly width: number
  readonly height: number
  readonly bitmap: ImageBitmap
}

export interface BrowserImageRenderDescription {
  readonly assetId: number
  readonly contentHash: string
  readonly width: number
  readonly height: number
}

export interface ImageRepresentationStore {
  acquireRepresentation(
    assetId: number,
    owner: string,
    bytes: number,
    releaseRepresentation: () => void,
  ): BrowserImageLease<undefined> | null
  discardRepresentation(contentHash: string, owner: string): boolean
}

interface BudgetEntry {
  readonly key: string
  readonly contentHash: string
  readonly owner: string
  readonly bytes: number
  leases: number
  lastUsed: number
  releaseRepresentation: () => void
}

class BrowserImageBudgetLease<T> implements BrowserImageLease<T> {
  #released = false

  constructor(
    readonly value: T,
    private readonly owner: object,
    private readonly releaseLease: () => void,
    private readonly discardLease: () => boolean,
  ) {}

  release(): void {
    if (this.#released) return
    this.#released = true
    this.releaseLease()
  }

  discard(owner: object): boolean {
    if (this.#released || this.owner !== owner) return false
    this.#released = true
    return this.discardLease()
  }
}

export class BrowserImageBudget {
  readonly #entries = new Map<string, BudgetEntry>()
  readonly #leaseOwner = {}
  #bytes = 0
  #clock = 0
  #clearing = false
  #releasing = false

  constructor(readonly limit = GRID_IMAGE_DECODED_BUDGET_BYTES) {}

  get bytes(): number { return this.#bytes }
  get representationCount(): number { return this.#entries.size }

  acquire<T>(
    contentHash: string,
    owner: string,
    bytes: number,
    value: T,
    releaseRepresentation: () => void,
  ): BrowserImageLease<T> | null {
    if (this.#clearing || this.#releasing ||
        contentHash.length === 0 || owner.length === 0 ||
        !Number.isSafeInteger(bytes) ||
        bytes <= 0 || bytes > this.limit) return null
    const key = this.#key(contentHash, owner)
    const existing = this.#entries.get(key)
    if (existing !== undefined) {
      if (existing.bytes !== bytes) return null
      existing.leases++
      existing.lastUsed = ++this.#clock
      return this.#lease(existing, value)
    }
    while (this.#bytes > this.limit - bytes) {
      let oldest: BudgetEntry | undefined
      let sameContent: BudgetEntry | undefined
      for (const entry of this.#entries.values()) {
        if (entry.leases !== 0) continue
        if (oldest === undefined || entry.lastUsed < oldest.lastUsed) oldest = entry
        // Bitmap-to-texture transfer must replace its just-released source;
        // evicting an older texture here would shrink an exact-fit cache.
        if (entry.contentHash === contentHash &&
            (sameContent === undefined || entry.lastUsed < sameContent.lastUsed)) {
          sameContent = entry
        }
      }
      const victim = sameContent ?? oldest
      if (victim === undefined) return null
      this.#remove(victim)
    }
    const entry: BudgetEntry = {
      key, contentHash, owner, bytes, leases: 1, lastUsed: ++this.#clock,
      releaseRepresentation,
    }
    this.#entries.set(key, entry)
    this.#bytes += bytes
    return this.#lease(entry, value)
  }

  setRelease(contentHash: string, owner: string, releaseRepresentation: () => void): boolean {
    const entry = this.#entries.get(this.#key(contentHash, owner))
    if (entry === undefined) return false
    entry.releaseRepresentation = releaseRepresentation
    return true
  }

  discard(contentHash: string, owner: string): boolean {
    const entry = this.#entries.get(this.#key(contentHash, owner))
    if (entry === undefined) return false
    this.#remove(entry)
    return true
  }

  discardLease(lease: BrowserImageLease<unknown>): boolean {
    if (!(lease instanceof BrowserImageBudgetLease)) return false
    return lease.discard(this.#leaseOwner)
  }

  clear(): void {
    if (this.#clearing) return
    const entries = Array.from(this.#entries.values())
    this.#entries.clear()
    this.#bytes = 0
    this.#clearing = true
    let failed = false
    let failure: unknown
    try {
      for (const entry of entries) {
        try {
          this.#release(entry)
        } catch (error) {
          if (!failed) {
            failed = true
            failure = error
          }
        }
      }
    } finally {
      this.#entries.clear()
      this.#bytes = 0
      this.#clearing = false
    }
    if (failed) throw failure
  }

  #lease<T>(entry: BudgetEntry, value: T): BrowserImageLease<T> {
    return new BrowserImageBudgetLease(
      value,
      this.#leaseOwner,
      () => {
        const current = this.#entries.get(entry.key)
        if (current !== entry || entry.leases === 0) return
        entry.leases--
        entry.lastUsed = ++this.#clock
      },
      () => {
        if (this.#entries.get(entry.key) !== entry) return false
        this.#remove(entry)
        return true
      },
    )
  }

  #remove(entry: BudgetEntry): void {
    if (this.#entries.get(entry.key) !== entry) return
    this.#entries.delete(entry.key)
    this.#bytes -= entry.bytes
    this.#release(entry)
  }

  #release(entry: BudgetEntry): void {
    const alreadyReleasing = this.#releasing
    this.#releasing = true
    try {
      entry.releaseRepresentation()
    } finally {
      this.#releasing = alreadyReleasing
    }
  }

  #key(contentHash: string, owner: string): string {
    return `${contentHash}\u0000${owner}`
  }
}

interface StoredAsset {
  readonly assetEpoch: number
  readonly assetId: number
  readonly width: number
  readonly height: number
  readonly format: 1
  readonly sha256: Uint8Array
  readonly contentHash: string
  readonly bytes: Uint8Array<ArrayBuffer>
}

interface StoredContent {
  readonly contentHash: string
  readonly width: number
  readonly height: number
  readonly bytes: Uint8Array<ArrayBuffer>
  readonly assetIds: Set<number>
  readonly pendingAssetIds: Set<number>
  state: 'idle' | 'pending' | 'ready' | 'failed'
  bitmap?: ImageBitmap
  decode?: Promise<void>
}

interface ReservationState {
  readonly allocation: ImageAssetAllocation
  readonly assetSlots: number
  readonly encodedBytes: number
  claimed: boolean
}

interface ReservationCost {
  readonly assetSlots: number
  readonly encodedBytes: number
}

class BrowserStoreImageReservation implements ImageAssetReservation {
  #released = false

  constructor(
    readonly owner: BrowserImageStore,
    readonly session: number,
    private readonly releaseReservation: () => void,
  ) {}

  release(): void {
    if (this.#released) return
    this.#released = true
    this.releaseReservation()
  }
}

export interface BrowserImageStoreOptions {
  readonly subtle?: Pick<SubtleCrypto, 'digest'>
  readonly createImageBitmap?: (blob: Blob) => Promise<ImageBitmap>
  readonly onStateChange?: (event: BrowserImageStateEvent) => void
  readonly onDiagnostic?: (message: string) => void
  readonly onCapacityAvailable?: () => void
}

export interface BrowserImageStoreStats {
  readonly activeEpoch: number
  readonly assetCount: number
  readonly encodedBytes: number
  readonly decodedBytes: number
  readonly representationCount: number
}

export interface GridImageStore {
  readonly activeEpoch: number
  hasAsset(assetId: number): boolean
  canAllocate(allocation: ImageAssetAllocation): boolean
  reserve(allocation: ImageAssetAllocation): ImageAssetReservation | null
  // A successful reservation transfers record.bytes into the store. Exact
  // ArrayBuffer-backed views are adopted; other views are copied exactly.
  accept(
    record: DecodedImageAssetRecord,
    reservation?: ImageAssetReservation | null,
  ): Promise<'stored' | 'duplicate'>
  describeForRender?(assetId: number): BrowserImageRenderDescription | null
  acquireForRender?(assetId: number): BrowserImageLease<BrowserImageRenderAsset> | null
  acquireRepresentation?(
    assetId: number,
    owner: string,
    bytes: number,
    releaseRepresentation: () => void,
  ): BrowserImageLease<undefined> | null
  discardRepresentation?(contentHash: string, owner: string): boolean
  requestIdleNotification(): boolean
  setCapacityListener(listener?: () => void): void
  reset(epoch: number): void
  resetForReconnect(): void
  dispose(): void
}

export class BrowserImageStore implements GridImageStore {
  readonly #subtle: Pick<SubtleCrypto, 'digest'>
  readonly #createImageBitmap: (blob: Blob) => Promise<ImageBitmap>
  readonly #onStateChange?: (event: BrowserImageStateEvent) => void
  readonly #onDiagnostic?: (message: string) => void
  #onCapacityAvailable?: () => void
  readonly #assets = new Map<number, StoredAsset>()
  readonly #contents = new Map<string, StoredContent>()
  readonly #reservations = new Map<BrowserStoreImageReservation, ReservationState>()
  readonly #budget = new BrowserImageBudget()
  #activeEpoch = 0
  #encodedBytes = 0
  #reservedAssetCount = 0
  #reservedEncodedBytes = 0
  #verificationBytes = 0
  #peakVerificationBytes = 0
  #blockedAllocation?: ImageAssetAllocation
  #idleNotificationPending = false
  #session = 0
  #disposed = false

  constructor(options: BrowserImageStoreOptions = {}) {
    this.#subtle = options.subtle ?? crypto.subtle
    this.#createImageBitmap = options.createImageBitmap ?? ((blob) => globalThis.createImageBitmap(blob, {
      premultiplyAlpha: 'none',
      colorSpaceConversion: 'none',
    }))
    this.#onStateChange = options.onStateChange
    this.#onDiagnostic = options.onDiagnostic
    this.#onCapacityAvailable = options.onCapacityAvailable
  }

  get activeEpoch(): number { return this.#activeEpoch }
  get assetCount(): number { return this.#assets.size }
  get encodedBytes(): number { return this.#encodedBytes }
  get reservationCount(): number { return this.#reservations.size }
  get reservedEncodedBytes(): number { return this.#reservedEncodedBytes }
  get verificationBytes(): number { return this.#verificationBytes }
  get peakVerificationBytes(): number { return this.#peakVerificationBytes }
  get decodedBytes(): number { return this.#budget.bytes }
  get representationCount(): number { return this.#budget.representationCount }
  hasAsset(assetId: number): boolean {
    const asset = this.#assets.get(assetId)
    return asset !== undefined && asset.assetEpoch === this.#activeEpoch
  }
  get stats(): BrowserImageStoreStats {
    return {
      activeEpoch: this.#activeEpoch,
      assetCount: this.assetCount,
      encodedBytes: this.encodedBytes,
      decodedBytes: this.decodedBytes,
      representationCount: this.representationCount,
    }
  }

  setCapacityListener(listener?: () => void): void {
    this.#onCapacityAvailable = listener
  }

  requestIdleNotification(): boolean {
    if (this.#disposed || this.#activeEpoch === 0 || this.#reservations.size === 0) return false
    this.#idleNotificationPending = true
    return true
  }

  canAllocate(allocation: ImageAssetAllocation): boolean {
    const cost = this.#reservationCost(allocation)
    return cost !== undefined && this.#fits(cost)
  }

  reserve(allocation: ImageAssetAllocation): ImageAssetReservation | null {
    const cost = this.#reservationCost(allocation, true)
    if (cost === undefined) return null
    if (!this.#retainedFits(cost)) {
      throw protocol('asset', 'active retained image asset capacity exceeded')
    }
    if ((cost.assetSlots !== 0 || cost.encodedBytes !== 0) &&
        this.#blockedAllocation !== undefined && this.#reservations.size !== 0) {
      return null
    }
    if (!this.#fits(cost)) {
      this.#blockedAllocation = copyAllocation(allocation)
      return null
    }
    const reservedAllocation = copyAllocation(allocation)
    let reservation!: BrowserStoreImageReservation
    reservation = new BrowserStoreImageReservation(
      this,
      this.#session,
      () => this.#releaseReservation(reservation),
    )
    this.#reservations.set(reservation, {
      allocation: reservedAllocation,
      assetSlots: cost.assetSlots,
      encodedBytes: cost.encodedBytes,
      claimed: false,
    })
    this.#reservedAssetCount += cost.assetSlots
    this.#reservedEncodedBytes += cost.encodedBytes
    return reservation
  }

  async accept(
    record: DecodedImageAssetRecord,
    providedReservation?: ImageAssetReservation | null,
  ): Promise<'stored' | 'duplicate'> {
    try {
      this.#validateRecord(record)
    } catch (error) {
      providedReservation?.release()
      throw error
    }
    const allocation: ImageAssetAllocation = {
      assetEpoch: record.assetEpoch,
      assetId: record.assetId,
      width: record.width,
      height: record.height,
      format: record.format,
      sha256: record.sha256,
      encodedLength: record.bytes.length,
      logicalLength: 56 + record.bytes.length,
    }
    const candidate = providedReservation ?? this.reserve(allocation)
    if (candidate === null || candidate === undefined) {
      throw protocol('asset', 'active image asset capacity exceeded')
    }
    const { reservation, state } = this.#claimReservation(candidate, allocation)
    try {
      const existing = this.#assets.get(record.assetId)
      if (existing !== undefined) {
        if (this.#sameRecord(existing, record)) return 'duplicate'
        throw protocol('asset', 'image asset ID was reused with different content')
      }
      validatePng(record.bytes, record.width, record.height)
      const verifiedBytes = ownedBrowserBytes(record.bytes)
      const expectedHash = state.allocation.sha256
      this.#verificationBytes += verifiedBytes.length
      this.#peakVerificationBytes = Math.max(this.#peakVerificationBytes, this.#verificationBytes)
      let actualHash: Uint8Array
      try {
        try {
          actualHash = new Uint8Array(await this.#subtle.digest('SHA-256', verifiedBytes))
        } catch (error) {
          if (reservation.session !== this.#session ||
              this.#reservations.get(reservation) !== state) {
            throw new BrowserImageStaleOperationError()
          }
          throw error
        }
      } finally {
        this.#verificationBytes -= verifiedBytes.length
      }
      if (state === undefined || reservation.session !== this.#session ||
          this.#reservations.get(reservation) !== state) {
        throw new BrowserImageStaleOperationError()
      }
      if (!equalBytes(actualHash, expectedHash)) throw protocol('decode', 'image asset SHA-256 mismatch')

      const raced = this.#assets.get(record.assetId)
      if (raced !== undefined) {
        if (this.#sameRecord(raced, { ...record, sha256: expectedHash, bytes: verifiedBytes })) {
          return 'duplicate'
        }
        throw protocol('asset', 'image asset ID was reused with different content')
      }

      const contentHash = hex(expectedHash)
      let content = this.#contents.get(contentHash)
      if (content !== undefined && (content.width !== record.width || content.height !== record.height ||
          !equalBytes(content.bytes, verifiedBytes))) {
        throw protocol('asset', 'image content hash collision')
      }
      const bytes = verifiedBytes
      const stored: StoredAsset = {
        assetEpoch: record.assetEpoch,
        assetId: record.assetId,
        width: record.width,
        height: record.height,
        format: 1,
        sha256: expectedHash.slice(),
        contentHash,
        bytes,
      }
      if (content === undefined) {
        content = {
          contentHash, width: record.width, height: record.height, bytes,
          assetIds: new Set(), pendingAssetIds: new Set(), state: 'idle',
        }
        this.#contents.set(contentHash, content)
      }
      content.assetIds.add(record.assetId)
      this.#assets.set(record.assetId, stored)
      this.#encodedBytes += bytes.length
      this.#releaseReservation(reservation, true)
      return 'stored'
    } finally {
      this.#releaseReservation(reservation, true)
    }
  }

  async acquireBitmap(assetId: number): Promise<BrowserImageLease<ImageBitmap> | null> {
    const asset = this.#assets.get(assetId)
    if (asset === undefined || asset.assetEpoch !== this.#activeEpoch) return null
    const content = this.#contents.get(asset.contentHash)
    if (content === undefined || content.state === 'failed') return null
    if (content.state === 'idle') this.#startDecode(content, assetId)
    else if (content.state === 'pending') this.#markPending(content, assetId)
    await content.decode
    if (content.state !== 'ready' || content.bitmap === undefined) return null
    const decodedBytes = checkedDecodedBytes(content.width, content.height)
    return this.#budget.acquire(
      content.contentHash, 'bitmap', decodedBytes, content.bitmap,
      () => this.#evictBitmap(content),
    )
  }

  describeForRender(assetId: number): BrowserImageRenderDescription | null {
    const asset = this.#assets.get(assetId)
    if (asset === undefined || asset.assetEpoch !== this.#activeEpoch) return null
    return Object.freeze({
      assetId,
      contentHash: asset.contentHash,
      width: asset.width,
      height: asset.height,
    })
  }

  acquireForRender(assetId: number): BrowserImageLease<BrowserImageRenderAsset> | null {
    const asset = this.#assets.get(assetId)
    if (asset === undefined || asset.assetEpoch !== this.#activeEpoch) return null
    const content = this.#contents.get(asset.contentHash)
    if (content === undefined || content.state === 'failed') return null
    if (content.state === 'idle') {
      this.#startDecode(content, assetId)
      return null
    }
    if (content.state !== 'ready' || content.bitmap === undefined) {
      this.#markPending(content, assetId)
      return null
    }
    const value: BrowserImageRenderAsset = Object.freeze({
      assetId,
      contentHash: content.contentHash,
      width: content.width,
      height: content.height,
      bitmap: content.bitmap,
    })
    return this.#budget.acquire(
      content.contentHash,
      'bitmap',
      checkedDecodedBytes(content.width, content.height),
      value,
      () => this.#evictBitmap(content),
    )
  }

  acquireRepresentation(
    assetId: number,
    owner: string,
    bytes: number,
    releaseRepresentation: () => void,
  ): BrowserImageLease<undefined> | null {
    const asset = this.#assets.get(assetId)
    if (asset === undefined || asset.assetEpoch !== this.#activeEpoch || owner.length === 0) return null
    return this.#budget.acquire(
      asset.contentHash, `renderer:${owner}`, bytes, undefined, releaseRepresentation,
    )
  }

  discardRepresentation(contentHash: string, owner: string): boolean {
    if (!/^[0-9a-f]{64}$/u.test(contentHash) || owner.length === 0) return false
    return this.#budget.discard(contentHash, `renderer:${owner}`)
  }

  reset(epoch: number): void {
    if (this.#disposed) return
    if (!uint32Nonzero(epoch)) throw protocol('generation', 'invalid image asset epoch')
    this.#clear(epoch)
  }

  resetForReconnect(): void {
    if (this.#disposed) return
    this.#clear(0)
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#clear(0)
  }

  #startDecode(content: StoredContent, assetId: number): void {
    const decodedBytes = checkedDecodedBytes(content.width, content.height)
    const reservation = this.#budget.acquire(
      content.contentHash, 'bitmap', decodedBytes, undefined,
      () => {},
    )
    if (reservation === null) return
    content.state = 'pending'
    this.#markPending(content, assetId)
    const session = this.#session
    content.decode = this.#decode(content, reservation, session)
  }

  async #decode(
    content: StoredContent,
    reservation: BrowserImageLease<undefined>,
    session: number,
  ): Promise<void> {
    try {
      const decoded = await this.#createImageBitmap(new Blob([content.bytes], { type: 'image/png' }))
      if (session !== this.#session || this.#contents.get(content.contentHash) !== content) {
        decoded.close()
        reservation.release()
        return
      }
      if (decoded.width !== content.width || decoded.height !== content.height) {
        decoded.close()
        throw new Error('decoded image dimensions differ from validated PNG')
      }
      content.bitmap = decoded
      content.state = 'ready'
      this.#budget.setRelease(content.contentHash, 'bitmap', () => this.#evictBitmap(content))
      reservation.release()
      this.#notifyPending(content, 'success')
    } catch {
      this.#budget.discardLease(reservation)
      if (session !== this.#session || this.#contents.get(content.contentHash) !== content) return
      content.bitmap = undefined
      content.state = 'failed'
      content.decode = undefined
      this.#notifyPending(content, 'failure')
      this.#onDiagnostic?.(boundedDiagnostic(
        `image-v1 platform decode failed for asset hash ${content.contentHash}`,
      ))
    }
  }

  #markPending(content: StoredContent, assetId: number): void {
    if (content.pendingAssetIds.has(assetId)) return
    content.pendingAssetIds.add(assetId)
    this.#notify(content, assetId, 'pending')
  }

  #notifyPending(content: StoredContent, state: 'success' | 'failure'): void {
    for (const assetId of content.pendingAssetIds) this.#notify(content, assetId, state)
    content.pendingAssetIds.clear()
  }

  #notify(content: StoredContent, assetId: number, state: BrowserImageState): void {
    this.#onStateChange?.({
      assetEpoch: this.#activeEpoch,
      assetId,
      contentHash: content.contentHash,
      state,
    })
  }

  #evictBitmap(content: StoredContent): void {
    content.bitmap?.close()
    content.bitmap = undefined
    if (content.state === 'ready') content.state = 'idle'
    content.decode = undefined
  }

  #clear(epoch: number): void {
    ++this.#session
    this.#budget.clear()
    this.#blockedAllocation = undefined
    this.#idleNotificationPending = false
    for (const [reservation, state] of this.#reservations) {
      if (!state.claimed) reservation.release()
    }
    this.#assets.clear()
    this.#contents.clear()
    this.#encodedBytes = 0
    this.#activeEpoch = epoch
  }

  #validateRecord(record: DecodedImageAssetRecord): void {
    if (this.#disposed || !uint32Nonzero(record.assetEpoch) || record.assetEpoch !== this.#activeEpoch) {
      throw protocol('generation', 'image asset epoch does not match the active epoch')
    }
    if (!uint32Nonzero(record.assetId) || record.format !== 1 ||
        !dimension(record.width) || !dimension(record.height) ||
        record.sha256.length !== 32 || record.bytes.length === 0 ||
        record.bytes.length > GRID_IMAGE_ASSET_SOURCE_MAX_BYTES) {
      throw protocol('decode', 'invalid image asset metadata')
    }
  }

  #validAllocation(allocation: ImageAssetAllocation): boolean {
    return !this.#disposed && allocation.assetEpoch === this.#activeEpoch &&
      uint32Nonzero(allocation.assetId) && allocation.format === 1 &&
      dimension(allocation.width) && dimension(allocation.height) &&
      allocation.sha256.length === 32 && allocation.encodedLength > 0 &&
      allocation.encodedLength <= GRID_IMAGE_ASSET_SOURCE_MAX_BYTES &&
      allocation.logicalLength === 56 + allocation.encodedLength
  }

  #reservationCost(
    allocation: ImageAssetAllocation,
    rejectConflicts = false,
  ): ReservationCost | undefined {
    if (!this.#validAllocation(allocation)) return undefined
    const existing = this.#assets.get(allocation.assetId)
    if (existing !== undefined) {
      if (existing.assetEpoch !== allocation.assetEpoch || existing.width !== allocation.width ||
          existing.height !== allocation.height || existing.format !== allocation.format ||
          existing.bytes.length !== allocation.encodedLength ||
          !equalBytes(existing.sha256, allocation.sha256)) {
        if (rejectConflicts) throw protocol('asset', 'image asset ID metadata does not match retained content')
        return undefined
      }
      return { assetSlots: 0, encodedBytes: 0 }
    }
    for (const [reservation, state] of this.#reservations) {
      if (reservation.session === this.#session &&
          state.allocation.assetEpoch === allocation.assetEpoch &&
          state.allocation.assetId === allocation.assetId &&
          !sameAllocation(state.allocation, allocation)) {
        if (rejectConflicts) throw protocol('asset', 'image asset ID metadata changed while pending')
        return undefined
      }
    }
    return { assetSlots: 1, encodedBytes: allocation.encodedLength }
  }

  #fits(cost: ReservationCost): boolean {
    return this.#assets.size + this.#reservedAssetCount <= GRID_IMAGE_MAX_ASSETS - cost.assetSlots &&
      this.#encodedBytes + this.#reservedEncodedBytes <=
        GRID_IMAGE_ENCODED_BUDGET_BYTES - cost.encodedBytes
  }

  #retainedFits(cost: ReservationCost): boolean {
    return this.#assets.size <= GRID_IMAGE_MAX_ASSETS - cost.assetSlots &&
      this.#encodedBytes <= GRID_IMAGE_ENCODED_BUDGET_BYTES - cost.encodedBytes
  }

  #claimReservation(
    reservation: ImageAssetReservation,
    allocation: ImageAssetAllocation,
  ): { reservation: BrowserStoreImageReservation; state: ReservationState } {
    if (!(reservation instanceof BrowserStoreImageReservation) || reservation.owner !== this) {
      reservation.release()
      throw protocol('asset', 'image asset reservation does not belong to this store')
    }
    if (reservation.session !== this.#session) throw new BrowserImageStaleOperationError()
    const state = this.#reservations.get(reservation)
    if (state === undefined || state.claimed || !sameAllocation(state.allocation, allocation)) {
      reservation.release()
      throw protocol('asset', 'invalid image asset reservation')
    }
    state.claimed = true
    return { reservation, state }
  }

  #releaseReservation(reservation: BrowserStoreImageReservation, allowClaimed = false): void {
    const state = this.#reservations.get(reservation)
    if (state === undefined || (state.claimed && !allowClaimed)) return
    this.#reservations.delete(reservation)
    this.#reservedAssetCount -= state.assetSlots
    this.#reservedEncodedBytes -= state.encodedBytes
    this.#notifyCapacityAvailable()
  }

  #notifyCapacityAvailable(): void {
    const allocation = this.#blockedAllocation
    if (this.#disposed || this.#reservations.size !== 0) return
    const notifyIdle = this.#idleNotificationPending
    const notifyCapacity = allocation !== undefined && allocation.assetEpoch === this.#activeEpoch
    if (!notifyIdle && !notifyCapacity) return
    this.#idleNotificationPending = false
    if (notifyCapacity) this.#blockedAllocation = undefined
    try {
      this.#onCapacityAvailable?.()
    } catch {
      // A repaint/recovery callback cannot be allowed to corrupt quota release.
    }
  }

  #sameRecord(existing: StoredAsset, record: DecodedImageAssetRecord): boolean {
    return existing.assetEpoch === record.assetEpoch && existing.assetId === record.assetId &&
      existing.width === record.width && existing.height === record.height &&
      existing.format === record.format && equalBytes(existing.sha256, record.sha256) &&
      equalBytes(existing.bytes, record.bytes)
  }
}

function sameAllocation(a: ImageAssetAllocation, b: ImageAssetAllocation): boolean {
  return a.assetEpoch === b.assetEpoch && a.assetId === b.assetId &&
    a.width === b.width && a.height === b.height && a.format === b.format &&
    a.encodedLength === b.encodedLength && a.logicalLength === b.logicalLength &&
    equalBytes(a.sha256, b.sha256)
}

function copyAllocation(allocation: ImageAssetAllocation): ImageAssetAllocation {
  return { ...allocation, sha256: browserBytes(allocation.sha256) }
}

function protocol(reason: BrowserImageProtocolReason, message: string): BrowserImageProtocolError {
  return new BrowserImageProtocolError(reason, message)
}

function uint32Nonzero(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= 0xffffffff
}

function dimension(value: number): boolean {
  return Number.isInteger(value) && value >= 1 && value <= GRID_IMAGE_MAX_DIMENSION
}

function checkedDecodedBytes(width: number, height: number): number {
  const pixels = width * height
  const bytes = pixels * 4
  if (!Number.isSafeInteger(pixels) || !Number.isSafeInteger(bytes) || bytes <= 0) {
    throw protocol('decode', 'decoded image byte size overflow')
  }
  return bytes
}

function equalBytes(a: Uint8Array, b: Uint8Array): boolean {
  if (a.length !== b.length) return false
  let difference = 0
  for (let index = 0; index < a.length; index++) difference |= a[index] ^ b[index]
  return difference === 0
}

function browserBytes(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  const copy = new Uint8Array(new ArrayBuffer(bytes.length))
  copy.set(bytes)
  return copy
}

function ownedBrowserBytes(bytes: Uint8Array): Uint8Array<ArrayBuffer> {
  if (bytes.buffer instanceof ArrayBuffer && bytes.byteOffset === 0 &&
      bytes.byteLength === bytes.buffer.byteLength) {
    return bytes as Uint8Array<ArrayBuffer>
  }
  return browserBytes(bytes)
}

function hex(bytes: Uint8Array): string {
  let value = ''
  for (const byte of bytes) value += byte.toString(16).padStart(2, '0')
  return value
}

function boundedDiagnostic(message: string): string {
  return message.slice(0, 256)
}

const PNG_SIGNATURE = new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])
let crcTable: Uint32Array | undefined

function crc32(bytes: Uint8Array): number {
  if (crcTable === undefined) {
    crcTable = new Uint32Array(256)
    for (let value = 0; value < 256; value++) {
      let crc = value
      for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
      crcTable[value] = crc >>> 0
    }
  }
  let crc = 0xffffffff
  for (const byte of bytes) crc = (crc >>> 8) ^ crcTable[(crc ^ byte) & 0xff]
  return (crc ^ 0xffffffff) >>> 0
}

function readU32(bytes: Uint8Array, offset: number): number {
  return bytes[offset] * 0x1000000 + (bytes[offset + 1] << 16) +
    (bytes[offset + 2] << 8) + bytes[offset + 3]
}

function typeEquals(bytes: Uint8Array, offset: number, name: string): boolean {
  for (let index = 0; index < 4; index++) {
    if (bytes[offset + index] !== name.charCodeAt(index)) return false
  }
  return true
}

function validatePng(bytes: Uint8Array, expectedWidth: number, expectedHeight: number): void {
  if (bytes.length > GRID_IMAGE_ASSET_SOURCE_MAX_BYTES || bytes.length < PNG_SIGNATURE.length ||
      !equalBytes(bytes.subarray(0, PNG_SIGNATURE.length), PNG_SIGNATURE)) {
    throw protocol('decode', 'invalid PNG signature')
  }
  let offset = PNG_SIGNATURE.length
  let sawHeader = false
  let sawPalette = false
  let sawData = false
  let dataEnded = false
  let bitDepth = 0
  let colorType = 0
  while (offset < bytes.length) {
    if (bytes.length - offset < 12) throw protocol('decode', 'truncated PNG chunk')
    const length = readU32(bytes, offset)
    if (length > bytes.length - offset - 12) throw protocol('decode', 'truncated PNG chunk data')
    const typeOffset = offset + 4
    const dataOffset = offset + 8
    const crcOffset = dataOffset + length
    for (let index = 0; index < 4; index++) {
      const value = bytes[typeOffset + index]
      if (!((value >= 0x41 && value <= 0x5a) || (value >= 0x61 && value <= 0x7a))) {
        throw protocol('decode', 'invalid PNG chunk name')
      }
    }
    if (bytes[typeOffset + 2] < 0x41 || bytes[typeOffset + 2] > 0x5a) {
      throw protocol('decode', 'invalid PNG reserved chunk-name bit')
    }
    if (crc32(bytes.subarray(typeOffset, crcOffset)) !== readU32(bytes, crcOffset)) {
      throw protocol('decode', 'invalid PNG chunk CRC')
    }
    const isHeader = typeEquals(bytes, typeOffset, 'IHDR')
    const isPalette = typeEquals(bytes, typeOffset, 'PLTE')
    const isData = typeEquals(bytes, typeOffset, 'IDAT')
    const isEnd = typeEquals(bytes, typeOffset, 'IEND')
    if (!sawHeader) {
      if (!isHeader || length !== 13) throw protocol('decode', 'PNG IHDR must be first')
      const width = readU32(bytes, dataOffset)
      const height = readU32(bytes, dataOffset + 4)
      if (!dimension(width) || !dimension(height) || width !== expectedWidth || height !== expectedHeight) {
        throw protocol('decode', 'PNG dimensions do not match image asset metadata')
      }
      bitDepth = bytes[dataOffset + 8]
      colorType = bytes[dataOffset + 9]
      const legalDepth = colorType === 0
        ? [1, 2, 4, 8, 16].includes(bitDepth)
        : colorType === 3
          ? [1, 2, 4, 8].includes(bitDepth)
          : [2, 4, 6].includes(colorType) && [8, 16].includes(bitDepth)
      if (!legalDepth || bytes[dataOffset + 10] !== 0 || bytes[dataOffset + 11] !== 0 ||
          (bytes[dataOffset + 12] !== 0 && bytes[dataOffset + 12] !== 1)) {
        throw protocol('decode', 'invalid PNG IHDR encoding')
      }
      sawHeader = true
    } else if (isHeader) {
      throw protocol('decode', 'duplicate PNG IHDR')
    } else if (isPalette) {
      if (sawPalette || sawData || colorType === 0 || colorType === 4 || length === 0 ||
          length > 768 || length % 3 !== 0 || (colorType === 3 && length / 3 > 2 ** bitDepth)) {
        throw protocol('decode', 'invalid PNG palette')
      }
      sawPalette = true
    } else if (isData) {
      if (dataEnded || (colorType === 3 && !sawPalette)) throw protocol('decode', 'invalid PNG IDAT order')
      sawData = true
    } else if (isEnd) {
      if (length !== 0 || !sawData || offset + 12 !== bytes.length) {
        throw protocol('decode', 'invalid PNG IEND')
      }
      return
    } else {
      if ((bytes[typeOffset] & 0x20) === 0) throw protocol('decode', 'unknown critical PNG chunk')
      if (sawData) dataEnded = true
    }
    offset += length + 12
  }
  throw protocol('decode', 'PNG is missing IEND')
}
